<script lang="ts" setup>
const route = useRoute();
const registrationStore = useRegistrationStore();
const { t } = useI18n({
  useScope: 'local',
});

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
});
function reloadRegistration() {
  if (import.meta.client) {
    window.location.href = route.path;
  }
}
</script>

<template>
  <section class="registration-feedback">
    <div v-if="registrationStore.state.submitLoading" class="registration-feedback-loading">
      <video autoplay class="tw-loading-indicator" loop muted>
        <source type="video/webm" src="/assets/images/loading-indicator/swap-2houses/128x128.webm" />
      </video>
    </div>
    <div v-if="!registrationStore.state.submitLoading && !registrationStore.state.registrationError" class="registration-feedback-success">
      <h1>{{ t('welcome') }}</h1>
      <p v-if="registrationStore.state.numberOfFoundMatches">
        {{ t('found_matches_before') }}
        <strong>{{ registrationStore.state.numberOfFoundMatches }}</strong>
        {{ t('found_matches_after') }}
      </p>
      <p>
        {{ t('activation_message_part1') }}
        <span class="registration-feedback-success__bold">{{ t('activation_message_part2') }}</span>
        {{ t('activation_message_part3') }}
        <span class="registration-feedback-success__bold">{{ t('activation_message_part4') }}</span>
        {{ t('activation_message_part5') }}
      </p>
      <p>
        {{ t('check_email_part1') }}
        <span class="registration-feedback-success__bold">
          {{ t('check_email_part2') }}
        </span>
        {{ t('check_email_part3') }}
      </p>
      <p>
        {{ t('contact_message_part1') }}
        <a href="/support#formular">{{ t('contact_message_link') }}</a>
        {{ t('contact_message_part2') }}
      </p>
    </div>
    <div v-if="!registrationStore.state.submitLoading && registrationStore.state.registrationError" class="registration-feedback-error">
      <h1>{{ t('error_title') }}</h1>
      <p>{{ t('error_message') }}</p>
      <p>
        <tw-ui-button @click="reloadRegistration">{{ t('go_back') }}</tw-ui-button>
      </p>
      <p>
        {{ t('contact_message_part1') }}
        <a href="/support#formular">{{ t('contact_message_link') }}</a>
        {{ t('contact_message_part2') }}
      </p>
    </div>
  </section>
</template>

<style scoped lang="scss">
@import '@/assets/styles/tw-variables.scss';
@import '@/assets/styles/tw-mixins.scss';

#registration-main {
  padding-top: 20px !important;
}

h1 {
  text-transform: uppercase;
}

button {
  border-radius: 0.5em !important;
  padding: 1.5em 1em;
  font-size: 1.1rem;
  height: 3rem;
  text-transform: uppercase;
  @include tw-colorset($white, $primary, $primary);
  &.tw-tertiary {
    background-color: $white;
    color: $primary;
  }
  &.is--hovered {
    @include tw-colorset($white, #d47d00, #d47d00);
  }
}

#registration-main {
  border-top-right-radius: 0.5em;
}
.registration-feedback {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  &::before {
    position: absolute;
    content: '';
    display: block;
    width: 82px;
    height: 82px;
    background-color: $white;
    box-shadow: 1px 3px 4px 3px rgba(0, 0, 0, 0.15);
    top: -55px;
    left: 50%;
    transform: translate(-50%, -50%);
    background-image: url(/assets/images/logo/Tauschwohnung-Logo3-Orange.svg);
    background-position: 50%;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 50px;
    @include tw-is-desktop {
      top: -18px;
    }
  }
  &-loading {
    padding: 6.5rem 1.6rem;
    text-align: center;
  }
  a {
    color: $primary;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  &-success,
  &-error {
    h1 {
      margin: 0;
      font-size: 2rem;
      font-weight: bold;
    }
    padding: 0;
    text-align: center;
    @include tw-is-desktop {
      padding: 4rem 1.6rem;
    }
    &__bold {
      font-weight: bold;
    }
  }
}
</style>

<i18n lang="json">
{
  "de": {
    "welcome": "willkommen!",
    "found_matches_before": "Wir haben ",
    "found_matches_after": " Matches für dich gefunden \uD83E\uDD73",
    "activation_message_part1": "Bitte klicke auf den ",
    "activation_message_part2": "Aktivierungslink,",
    "activation_message_part3": " den wir Dir ",
    "activation_message_part4": "per E-Mail",
    "activation_message_part5": " gesendet haben!",
    "check_email_part1": "Falls du keine E-Mail im Postfach finden solltest, dann schau bitte auch in deinem ",
    "check_email_part2": "Spam Ordner",
    "check_email_part3": "nach.",
    "contact_message_part1": "Solltest du Schwierigkeiten haben, wende dich über das ",
    "contact_message_link": "Kontaktformular",
    "contact_message_part2": " an uns.",
    "error_title": "Es ist ein Fehler aufgetreten!",
    "error_message": "Bitte versuche es noch einmal",
    "go_back": "Zurück zum Formular"
  },
  "en": {
    "welcome": "welcome!",
    "found_matches_before": "Wir haben ",
    "found_matches_after": " Matches für dich gefunden \uD83E\uDD73",
    "activation_message_part1": "Please click on the ",
    "activation_message_part2": "activation link,",
    "activation_message_part3": " we have sent you",
    "activation_message_part4": "by e-mail!",
    "activation_message_part5": "",
    "check_email_part1": "If you cannot find an e-mail in your mailbox, please also check your ",
    "check_email_part2": "spam folder.",
    "check_email_part3": "",
    "contact_message_part1": "If you have any difficulties, please contact us using the ",
    "contact_message_link": "contact",
    "contact_message_part2": " form.",
    "error_title": "An error has occurred!",
    "error_message": "Please try again",
    "go_back": "Back to the form"
  }
}
</i18n>
